import {BitrixVue} from 'ui.vue3'
import {Loader} from '../../../components/ui/Loader'
import {VerificationManager,} from '../../../components/ui/input/VerificationManager'

export class Likes {
    #application = null
    #entityId = null
    #options = {}

    constructor(rootNode, entityId, options = {}) {
        this.rootNode = document.querySelector(rootNode)
        this.#entityId = entityId
        this.#options = options
    }

    start(): void {
        this.attachTemplate()
    }

    attachTemplate(): void {
        const context = this

        this.#application = BitrixVue.createApp({
            name: 'DzenLikeButton',
            components: {
                Loader,
                VerificationManager,
            },
            data() {
                return {
                    isLoading: false,
                    isProcessing: false,
                    likesCount: 0,
                    isLiked: false,
                    showVerification: false,
                    verification: {
                        inProcess: false,
                        verified: false,
                        userData: null,
                    },
                    userId: context.#options.userId || BX.message('USER_ID') || 0,
                }
            },
            computed: {
                isAuthenticated() {
                    return this.userId > 0
                },
                likeButtonClass() {
                    return {
                        'like-button': true,
                        'like-button--liked': this.isLiked,
                        'like-button--loading': this.isProcessing,
                    }
                },
            },
            methods: {
                async loadLikes() {
                    this.isLoading = true
                    try {
                        const response = await BX.ajax.runComponentAction('dzen:comments',
                            'getLikes', {
                                mode: 'ajax',
                                data: {
                                    elementId: context.#entityId,
                                    userId: this.isAuthenticated ? this.userId : null,
                                },
                            })

                        if (response.status === 'success') {
                            this.likesCount = response.data.likesCount || 0
                            this.isLiked = response.data.isLiked || false
                        }
                    } catch (error) {
                        console.error('Error loading likes:', error)
                    } finally {
                        this.isLoading = false
                    }
                },
                async toggleLike() {
                    if (this.isProcessing) return

                    // Для неавторизованных показываем верификацию
                    if (!this.isAuthenticated && !window.userVerified) {
                        this.showVerification = true
                        return
                    }

                    this.isProcessing = true
                    try {
                        if (this.isLiked) {
                            await this.removeLike()
                        } else {
                            await this.addLike()
                        }
                    } catch (error) {
                        console.error('Error toggling like:', error)
                        BX.UI.Notification.Center.notify({
                            content: 'Ошибка при обработке лайка: ' +
                                (error.message || 'Неизвестная ошибка'),
                            autoHideDelay: 5000,
                        })
                    } finally {
                        this.isProcessing = false
                    }
                },
                async addLike() {
                    const response = await BX.ajax.runComponentAction('dzen:comments',
                        'addLike', {
                            mode: 'ajax',
                            data: {
                                elementId: context.#entityId,
                                ...(!this.isAuthenticated && this.verification?.userData ? {
                                    userData: this.verification.userData,
                                } : {}),
                            },
                        })

                    if (response.status === 'success') {
                        this.likesCount = response.data.likesCount || this.likesCount + 1
                        this.isLiked = true
                        this.showVerification = false
                    }
                },
                async removeLike() {
                    const response = await BX.ajax.runComponentAction('dzen:comments',
                        'removeLike', {
                            mode: 'ajax',
                            data: {
                                elementId: context.#entityId,
                                userId: this.isAuthenticated ? this.userId : null,
                            },
                        })

                    if (response.status === 'success') {
                        this.likesCount = response.data.likesCount ||
                            Math.max(0, this.likesCount - 1)
                        this.isLiked = false
                    }
                },
                startVerification() {
                    this.verification.inProcess = true
                },
                handleVerificationComplete({verified, phone, email, name}) {
                    this.verification.verified = verified
                    this.verification.userData = {phone, email}
                    this.verification.inProcess = false

                    if (verified) {
                        this.toggleLike()
                    }
                },
                onVerificationError(error) {
                    this.verification.inProcess = false
                    console.error('Verification error:', error)
                    BX.UI.Notification.Center.notify({
                        content: 'Ошибка верификации: ' +
                            (error.message || 'Неизвестная ошибка'),
                        autoHideDelay: 5000,
                    })
                },
                closeVerification() {
                    this.showVerification = false
                    this.verification.inProcess = false
                },
            },
            mounted() {
                this.loadLikes()
            },
            template: `
        <div class="dzen-like-component">
          <Loader v-if="isLoading" />
          
          <button 
            :class="likeButtonClass"
            @click="toggleLike"
            :disabled="isProcessing"
          >
            <span class="like-icon"><svg xmlns="http://www.w3.org/2000/svg" width="30" height="26" viewBox="0 0 30 26" fill="none">
<path d="M14.7369 25.625L14.2584 25.2063C3.37324 16.3547 0.442627 13.2446 0.442627 8.16088C0.442627 3.97428 3.85171 0.625 7.97851 0.625C11.4474 0.625 13.4211 2.59868 14.7369 4.09389C16.0527 2.59868 18.0264 0.625 21.4953 0.625C25.6819 0.625 29.0312 4.03409 29.0312 8.16088C29.0312 13.2446 26.1005 16.3547 15.2154 25.2063L14.7369 25.625ZM7.97851 2.18002C4.68903 2.18002 1.99765 4.87141 1.99765 8.16088C1.99765 12.5269 4.74884 15.3977 14.7369 23.5915C24.7249 15.3977 27.4761 12.5269 27.4761 8.16088C27.4761 4.87141 24.7847 2.18002 21.4953 2.18002C18.5048 2.18002 16.89 3.97428 15.634 5.40969L14.7369 6.42644L13.8398 5.40969C12.5838 3.97428 10.9689 2.18002 7.97851 2.18002Z" fill="#484444"/>
</svg></span>
            <span class="like-count">{{ likesCount }}</span>
          </button>
          
          <div v-if="showVerification" class="like-verification-modal">
            <div class="like-verification-content">
              <button class="close-button" @click="closeVerification">×</button>
              <h3>Пожалуйста, пройдите верификацию</h3>
              <p>Чтобы поставить лайк, нужно подтвердить, что вы не робот</p>
              
              <VerificationManager
                :hide-email="true"
                :show-name-field="false"
                @verification-complete="handleVerificationComplete"
                @error="onVerificationError"
              />
            </div>
          </div>
        </div>
      `,
        })

        this.#application.mount(this.rootNode)
    }

    destroy() {
        if (this.#application) {
            this.#application.unmount()
            this.#application = null
        }
    }
}